/**
 * 卖家客服工作台 Vuex 模块
 */
import { request } from '@/assets/js/request'
import customerDefaultAvatar from '@/assets/images/customer.jpg'

/* ========== 音频 & 标题闪烁 – 与之前完全一致 ========== */
let audioContext = null
let titleFlashTimer = null
let originalTitle = document.title

/* ========== 状态 ========== */
export default {
  namespaced: true,

  state: {
    conversations: [],          // 当前会话列表（视图）
    conversationsLoaded: false,
    activeConversation: null,   // 当前激活会话
    messages: [],               // 当前会话消息
    messagesByConversation: {}, // ✅ 按会话 ID 存储消息 { conversationId: [messages] }
    messagesCache: {},          // 消息缓存 { conversationId: messages }
    buyerInfo: null,            // 客服资料
    wsConnected: false,
    audioUnlocked: false,
    conversationId: '',
    buyerName: '',
  },

  mutations: {
    /* ---- 会话列表 ---- */
    // SET_CONVERSATIONS(state, list) {
    //   const index = list.findIndex(item => item.id === state.conversationId);
    //   if (index > -1) {
    //     list.splice(0, 0, list.splice(index, 1)[0]);
    //   }
    //   state.conversations = mapConversations(list)
    //   state.buyerName = list[0].meta.sender.name
    // },
    SET_CONVERSATIONS(state, list) {
      // 过滤无效数据
      const validList = list.filter(item => {
        const meta = item.meta || {}
        const sender = meta.sender || {}
        return !!(item.inbox_name || sender.name)
      })
      
      const index = validList.findIndex(item => item.id === state.conversationId);
      if (index > -1) {
        validList.splice(0, 0, validList.splice(index, 1)[0]);
      }
      
      // 确保数据完整后再设置
      state.conversations = mapConversations(validList)
      state.conversationsLoaded = true  // 标记已加载完成
      
      if (validList[0]) {
        state.buyerName = validList[0].meta?.sender?.name || ''
      }
    },
    // 加载开始时重置标志
    SET_CONVERSATIONS_LOADING(state) {
      state.conversationsLoaded = false
    },
    
    RESET_STATE(state) {
      state.conversations = []
      state.conversationsLoaded = false  // 重置标志
      // ... 其他重置
    },
    UPDATE_CONVERSATION(state, conversation) {
      if (conversation.type) {
        state.conversationId = conversation.conversationId
      }
      // 增量更新/追加
      const idx = state.conversations.findIndex(c => c.id === conversation.id)
      const mapped = mapConversations([conversation])[0]
      if (idx !== -1) {
        state.conversations.splice(idx, 1, mapped)
      } else {
        state.conversations.unshift(mapped)
      }
    },
    REMOVE_CONVERSATION(state, id) {
      state.conversations = state.conversations.filter(c => c.id !== id)
      if (state.activeConversation?.id === id) state.activeConversation = null
      delete state.messagesCache[id]
    },

    /* ---- 激活会话 ---- */
    SET_ACTIVE_CONVERSATION(state, conversation) {
      state.activeConversation = conversation
    },
    // 设置 WebSocket 连接状态
    // SET_WS_CONNECTED(state, connected) {
    //   state.wsConnected = connected
    // },
    /* ---- 消息 ---- */
    SET_MESSAGES(state, { messages, conversationId }) {
      // 不再判断激活会话，直接写缓存 & 视图
      const mapped = messages.map(mapMessage)
      state.messages = mapped                    // 当前视图
      state.messagesCache[conversationId] = mapped
      updateConversationPreview(state, conversationId, mapped)
    },
    ADD_MESSAGE(state, message) {
      // 检查是否存在相同 id 或 tempId 的消息（防止重复）
      // if(!message.tempId&&message.from)return
      const exists = state.messages.some(m =>
        m.id === message.id || m.tempId === message.tempId || m.tempId === message.echo_id
      )
      if (exists) {
        return
      }
      const mappedMessage = mapMessage(message)
      state.messages = [...state.messages, mappedMessage]

      // 更新缓存
      if (state.activeConversation) {
        state.messagesCache[state.activeConversation.id] = state.messages
        // 更新预览
        updateConversationPreview(state, state.activeConversation.id, state.messages)
      }
    },
    PREPEND_MESSAGES(state, messages) {
      const mapped = messages.map(mapMessage)
      const filtered = mapped.filter(m => !state.messages.some(ex => ex.id === m.id))
      state.messages = [...filtered, ...state.messages]
      if (state.activeConversation) {
        state.messagesCache[state.activeConversation.id] = state.messages
      }
    },
    // 根据临时ID更新消息 (用于乐观更新)
    UPDATE_MESSAGE_BY_TEMP_ID(state, { tempId, message }) {
      // console.log(state, { tempId, message })      
      const idx = state.messages.findIndex(m => m.tempId === tempId)
      if (idx !== -1) {
        // 保留旧消息的某些字段，合并新消息
        const oldMsg = state.messages[idx]
        const mapped = mapMessage({
          ...message,
          // 确保 sender 信息完整
          sender: message.sender || oldMsg.sender
        })
        mapped.status = 'sent'  // 明确标记为已发送
        // 替换消息
        state.messages.splice(idx, 1, mapped)
        // 更新缓存
        if (state.activeConversation) {
          state.messagesCache[state.activeConversation.id] = state.messages
        }
      }
    },

    /* ---- 通用 ---- */
    SET_BUYER_INFO(state, info) {
      state.buyerInfo = info
    },
    SET_WS_CONNECTED(state, connected) {
      state.wsConnected = connected
    },
    SET_AUDIO_UNLOCKED(state, unlocked) {
      state.audioUnlocked = unlocked
    },

    // 简化的消息内容更新 mutation
    UPDATE_MESSAGE_CONTENT(state, { messageId, updates }) {
      const message = state.messages.find(m => m.id === messageId || m.messageUuid === messageId)
      if (message) {
        Object.assign(message, updates)
        // 创建新数组以触发 Vue 响应式更新
        state.messages = [...state.messages]
      }
    },

    RESET_STATE(state) {
      state.conversations = []
      state.activeConversation = null
      state.messages = []
      state.messagesCache = {}
      state.audioUnlocked = false
      state.wsConnected = false
    },
    // 清除指定会话的所有问题引导消息
    CLEAR_QUESTION_GUIDES(state, conversationId) {
      state.messages = state.messages.filter(msg => 
        !(msg.isQuestionGuide && msg.conversation_id === conversationId)
      )
    },
    
    // 删除指定消息
    REMOVE_MESSAGE(state, messageId) {
      const index = state.messages.findIndex(msg => msg.id === messageId)
      if (index !== -1) {
        state.messages.splice(index, 1)
      }
    }
  },

  actions: {
    async loadConversations({ commit }) {
      commit('SET_CONVERSATIONS_LOADING')  // 开始加载
      try {
        const res = await request({
          url: 'BUYER_CHATWOOT_CLIENT_LIST',
          method: 'post'
        })
        if (res.data.code === '000') {
          commit('SET_CONVERSATIONS', res.data.result || [])
        }
      } catch (e) {
        console.error('加载会话列表失败', e)
        throw e
      }
    },
    // 加载买家信息
    async loadBuyerInfo({ commit }) {
      try {
        const response = await request({ url: 'BUYER_CHATWOOT_CLIENT_API_INFO', method: 'post' })
        if (response.data.code === '000') {
          commit('SET_BUYER_INFO', response.data.result)
        }
      } catch (error) {
        console.error('加载用户信息失败:', error)
        throw error
      }
    },
    /* ---- 1. 加载会话列表（卖家） ---- */
    async loadConversations({ commit }) {
      try {
        const res = await request({
          url: 'BUYER_CHATWOOT_CLIENT_LIST',
          method: 'post'
        })
        if (res.data.code === '000') {
          commit('SET_CONVERSATIONS', res.data.result || [])
        }
      } catch (e) {
        console.error('加载会话列表失败', e)
        throw e
      }
    },

    /* ---- 2. 创建会话（卖家主动发起） ---- */
    async createConversation({ commit }, { memberId, inboxType, sceneType }) {
      try {
        const res = await request({
          url: 'BUYER_CHATWOOT_CLIENT_CONVERSATIONS',
          method: 'post',
          data: { memberId, inboxType, sceneType, isLoginType: true }
        })
        if (res.data.code === '000') {
          const conv = res.data.result
          conv.type = '1'
          commit('UPDATE_CONVERSATION', conv)
          return conv
        }
      } catch (e) {
        console.error('创建会话失败', e)
        throw e
      }
    },

    /* ---- 3. 选择会话 -> 加载消息 ---- */
    async selectConversation({ commit, dispatch }, conversation) {
      if (!conversation) {
        commit('SET_ACTIVE_CONVERSATION', null)
        return
      }
      commit('SET_ACTIVE_CONVERSATION', conversation)
      await dispatch('loadMessages', { conversationId: conversation.id })
      // 延迟标记已读（如需）
      setTimeout(() => dispatch('markAsRead', conversation.id), 300)
    },

    /* ---- 4. 加载历史消息 ---- */
    async loadMessages({ commit }, { conversationId, before = null }) {
      const res = await request({
        url: 'BUYER_CHATWOOT_MESSAGE_LIST',
        method: 'post',
        data: { conversationId, before }
      })
      if (res.data.code === '000') {
        const list = (res.data.result || []).map(mapMessage) // ← 这里必须 map
        if (before) {
          commit('PREPEND_MESSAGES', list)
        } else {
          commit('SET_MESSAGES', { messages: list, conversationId })
        }
        return list
      }
    },

    /* ---- 5. 发送消息 ---- */
    async sendMessage({ commit }, { conversationId, content, tempId }) {
      // console.log('发送消息:', { conversationId, content, tempId })
      try {
        const data = {
          "conversationId": conversationId,
          "content": content
        }
        if (tempId) {
          data.sourceId = tempId
        }
        const response = await request({
          url: "BUYER_CHATWOOT_MESSAGE_SEND",
          method: "post",
          data: data
        })
        if (response.data.code === '000') {
          const realMsg = response.data.result
          if (tempId && realMsg) {
            commit('UPDATE_MESSAGE_BY_TEMP_ID', {
              tempId,
              message: realMsg
            })
          }
          // return response.data.result
        } else {
          throw new Error(response.data.msg || '发送失败')
        }
      } catch (error) {
        console.error('发送消息失败:', error)
        if (tempId) {
          commit('UPDATE_MESSAGE_BY_TEMP_ID', {
            tempId,
            message: { status: 'failed' }
          })
        }
        throw error
      }
    },
    /* ---- 6. 标记已读（如需） ---- */
    async markAsRead({ commit, state }, conversationId) {
      // 本地立即清零，让 UI 无延迟
      // console.log(state.conversations,conversationId,'消息')
      const idx = state.conversations.findIndex(c => c.id === conversationId)
      if (idx !== -1) {
        const clone = { ...state.conversations[idx], unreadCount: 0 }
        state.conversations.splice(idx, 1, clone)
      }
      // 后端有已读接口，在这里再补调一次
      await request({ url: 'BUYER_CHATWOOT_CLIENT_API_CONVERSATONS_MARKREAD', data: { id: conversationId } })
    },

    /* ---- 7. 音频/通知  ---- */
    initAudioContext({ commit }) {
      if (audioContext) return
      try {
        const AC = window.AudioContext || window.webkitAudioContext
        if (!AC) return
        audioContext = new AC()
        const tryUnlock = () => {
          if (audioContext.state === 'suspended') {
            audioContext.resume().then(() => commit('SET_AUDIO_UNLOCKED', true))
          } else if (audioContext.state === 'running') {
            commit('SET_AUDIO_UNLOCKED', true)
          }
        }
        tryUnlock()
        document.addEventListener('click', tryUnlock, { once: true })
      } catch (e) {
        console.error('初始化音频上下文失败', e)
      }
    },
    startTitleFlash() {
      if (titleFlashTimer) return
      originalTitle = document.title
      let flag = true
      titleFlashTimer = setInterval(() => {
        document.title = flag ? '【新消息】' + originalTitle : '【　　　】' + originalTitle
        flag = !flag
      }, 500)
    },
    stopTitleFlash() {
      if (titleFlashTimer) {
        clearInterval(titleFlashTimer)
        titleFlashTimer = null
        document.title = originalTitle
      }
    },
    async notifyNewMessage({ dispatch, state }, message) {
      if (state.audioUnlocked) {
        try {
          if (audioContext?.state !== 'running') await audioContext?.resume()
          if (audioContext?.state === 'running') {
            const ctx = audioContext
            const osc1 = ctx.createOscillator()
            const g1 = ctx.createGain()
            osc1.connect(g1); g1.connect(ctx.destination)
            osc1.type = 'sine'; osc1.frequency.value = 659.25
            g1.gain.setValueAtTime(0.1, ctx.currentTime)
            g1.gain.exponentialRampToValueAtTime(0.00001, ctx.currentTime + 0.4)
            osc1.start(); osc1.stop(ctx.currentTime + 0.4)

            const osc2 = ctx.createOscillator()
            const g2 = ctx.createGain()
            osc2.connect(g2); g2.connect(ctx.destination)
            osc2.type = 'sine'; osc2.frequency.value = 523.25
            g2.gain.setValueAtTime(0.1, ctx.currentTime + 0.3)
            g2.gain.exponentialRampToValueAtTime(0.00001, ctx.currentTime + 1.5)
            osc2.start(ctx.currentTime + 0.3); osc2.stop(ctx.currentTime + 1.5)
          }
        } catch (e) {
          console.error('播放提示音失败', e)
        }
      }
      try {
        if (window.Notification && Notification.permission === 'granted' && document.hidden) {
          new Notification('新消息', {
            body: message.content || (message.attachments?.length ? '[文件]' : '收到一条新消息')
          })
        }
      } catch (e) {
        console.error('浏览器通知失败', e)
      }
    },

    /* ---- 8. WebSocket 推送入口 ---- */
    updateConversation({ commit }, conversation) {
      // commit('UPDATE_CONVERSATION', conversation)
    },
    addMessage({ commit }, message) {
      commit('ADD_MESSAGE', message)
    }
  },

  getters: {
    totalUnreadCount: state => state.conversations.reduce((sum, c) => sum + (c.unreadCount || 0), 0),

    /* ✅ Bot 流式消息支持 */
    // 获取当前活跃会话的消息
    activeMessages(state) {
      return state.messages || []
    },

    // 根据 UUID 查找消息
    findMessageByUuid: (state) => (messageUuid) => {
      return state.messages.find(m => m.messageUuid === messageUuid) || null
    }
  }
}

function mapConversations(list) {
  return list.map(conv => {
    // 确保 meta 对象存在
    const meta = conv.meta || {}
    const sender = meta.sender || {}

    let last = conv.last_non_activity_message || conv.messages?.find(m => m.message_type !== 2)
    let text = ''
    if (last) {
      if (last.content) text = last.content
      else if (last.attachments?.length) {
        const a = last.attachments[0]
        if (a.file_type === 'image') text = '[图片]'
        else if (a.file_type === 'audio') text = '[语音]'
        else if (a.file_type === 'video') text = '[视频]'
        else text = '[文件]'
      }
    }
    return {
      id: conv.id,
      contactName: conv.inbox_name || meta.sender?.name || '未知商家',  // 优先使用 inbox_name
      contactAvatar: conv.meta?.sender?.thumbnail || customerDefaultAvatar,
      lastMessage: text,
      lastMessageTime: last?.created_at
        ? formatTime(last.created_at * 1000)
        : '',
      unreadCount: conv.contact_unread_count || conv.unreadCount,
      status: conv.status,
      _raw: conv,
      inboxType: conv.inbox_type
    }
  })
}
// 替换整个 mapMessage 函数
function mapMessage(msg) {
  // 如果已经是映射过的格式，直接返回
  if (msg.messageType !== undefined && msg.sender?.name !== undefined) {
    return {
      ...msg,
      status: msg.status || 'sent',
      tempId: msg.tempId || msg.source_id || null
    }
  }
  // 处理原始后端消息
  const isOutgoing = msg.sender?.type === 'contact';
  return {
    id: msg.id,
    content: msg.content || '',
    messageType: isOutgoing ? 1 : 0,
    createdAt: (msg.created_at || msg.createdAt) * 1000,
    sender: {
      id: msg.sender?.id || (isOutgoing ? 'buyer' : 'shop'),
      name: msg.sender?.name || (isOutgoing ? '未知买家' : '未知商家'),
      type: msg.sender?.type || (isOutgoing ? 'contact' : 'user')
    },
    attachments: msg.attachments || [],
    status: msg.status || 'sent',
    tempId: msg.source_id || msg.tempId || null
  }
}
function updateConversationPreview(state, conversationId, messages) {
  // 若需实时刷新左侧最后一条消息可在此实现
}
function formatTime(ts) {
  const d = new Date(ts)
  const now = new Date()
  // 今天的消息：只显示时间
  if (d.toDateString() === now.toDateString()) {
    return d.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' })
  }
  // 昨天的消息：显示 "昨天 + 时间
  const yst = new Date(now)
  yst.setDate(yst.getDate() - 1)
  if (d.toDateString() === yst.toDateString()) {
    const time = d.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' })
    return `昨天 ${time}`
  }
  // 更早的消息：显示 "日期 + 时间"
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  const time = d.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' })
  return `${month}-${day} ${time}`
}